// GreenGlam Lookup — predictive search that renders results INTO the product grid
// (no popup). On query it swaps server-rendered product cards into the grid; an
// emptied query restores the original collection cards.
//
// ── SCOPE CHIPS — state model (epic fttx) ────────────────────────────────────
// ONE source of truth: THE URL. No transient scope state anywhere.
//   • Collection scope = the PATHNAME. Its chip renders server-side on
//     collection pages; removing it is a real state transition — pushState to
//     /collections/all (keeping filter.*/q) + in-place re-render of grid, nav
//     and header from the fetched page. It does not "come back on clear";
//     re-scoping = clicking a category (a real navigation).
//     Search scope rides as filter.p.m.product_data.collection="<title>" — a
//     metafield Shopify syncs nightly to collection membership (DE + EN),
//     locale-safe, exact 1:1 with the collection.
//   • Every other chip is a pure PROJECTION of one filter.* URL param (vendor,
//     product type, concern metafields like "bei Couperose"). Removing one is
//     a URL MUTATION routed to the current grid owner (see below); chips are
//     re-derived from the URL afterwards (#syncChipsFromURL, also fired on the
//     facets' FilterUpdate event) — chips, panel, grid and nav can't disagree.
//   • The QUERY is projected into the URL too (?q=, replaceState) — reload/
//     share always reproduces the visible state (?q= hydration already exists).
//   • Grid ownership is MODAL: empty input → the server/facets render owns the
//     grid; query active → this component owns it via /search fetches. Every
//     mutation goes through exactly ONE writer for the current mode.
//   • The cached original grid is stamped with its URL; clear restores it only
//     if the URL still matches, otherwise the browse grid is fetched fresh.
//
// Search source = the real /search results page ONLY (no predictive suggest.json
// gate). /search is fuzzy + searches the description, so it's the authority on
// what matches; a suggest pre-gate was stricter and produced false negatives
// (typos, ingredient terms in the description). No-match is read straight from the
// fetched grid: /search renders its fallback recommendations OUTSIDE .product-grid,
// so an empty .product-grid = a confirmed no-match. The grid is the gate.
//
// Why a page fetch, not a client filter: the grid only holds the first few loaded
// pages of a large catalog, so filtering visible cards can never surface matches
// deeper in the collection. The fetched page returns the true matches as the same
// .product-grid__item card markup, so the GG card styles apply unchanged.

// ── "Did you mean" condition-term correction ──────────────────────────────────
// Misspelled SKIN-CONDITION queries semantic-broaden into the wrong category (the
// typo "Cuperose" embeds into the perfume cluster → 112 false hits). We can't
// disable semantic search (no API or storefront toggle), so we silently rewrite a
// known misspelling to its CANONICAL spelling BEFORE searching — "Cuperose" fetches
// "Couperose" and returns the 7 right products, which the search-results.liquid
// condition guard then keeps. Word-level + case-insensitive, so multi-word queries
// keep their other words ("cuperose creme" → "couperose creme").
//
// Keep these spelling variants in sync with the `condition_groups` list in
// sections/search-results.liquid (same data, opposite direction).
const GG_CONDITION_CORRECTIONS = {
  cuperose: 'couperose', kuperose: 'couperose', couperos: 'couperose', cooperose: 'couperose',
  rosazea: 'rosacea', rosacia: 'rosacea', rosaca: 'rosacea',
  neurodermitits: 'neurodermitis', neurodermatitis: 'neurodermitis',
  acne: 'akne',
  zellulite: 'cellulite',
  roetungen: 'rötungen', roetung: 'rötung',
  ekzeme: 'ekzem', eczema: 'ekzem',
  // Synonym broadening: "schwanger" matches few products on its own (11), while
  // "schwangerschaft" returns the full set (113) that the search-results guard then
  // narrows to the "Sicher in der Schwangerschaft"-tagged (pregnancy-safe) products.
  schwanger: 'schwangerschaft',
};

function ggCorrectConditionQuery(query) {
  if (!query) return query;
  // LOCALE GATE: every entry above maps to a GERMAN canonical term, and a few
  // (acne→akne, eczema→ekzem) rewrite a correct ENGLISH word into German. Running
  // this on a non-German storefront corrupts valid English queries against the
  // English search index (e.g. "acne" → "akne" → fewer/zero hits). Only correct
  // on the German locale; pass every other locale's query through untouched.
  const locale = ((window.Shopify && window.Shopify.locale) || '').toLowerCase();
  if (!locale.startsWith('de')) return query;
  let changed = false;
  const corrected = query
    .split(/\s+/)
    .map((word) => {
      const fix = GG_CONDITION_CORRECTIONS[word.toLowerCase()];
      if (fix) {
        changed = true;
        return fix;
      }
      return word;
    })
    .join(' ');
  return changed ? corrected : query;
}

class GgLookupComponent extends HTMLElement {
  // sessionStorage key for the Shop Filter panel open state (persisted across
  // collection-switch navigations).
  static #FILTERS_OPEN_KEY = 'gg-shop-filters-open';

  #abortController = null;
  // Cached innerHTML of the untouched collection grid, captured before the first
  // query swap so an emptied query can restore the original cards. STAMPED with
  // the URL it was captured under (#originalGridURL): if the URL has changed
  // since (a filter chip was removed, a facet toggled), the cache is stale and
  // the clear path fetches a fresh browse grid instead of restoring it.
  #originalGridHTML = null;
  #originalGridURL = null;
  // Who owns the grid right now: 'browse' (server/facets render it) or
  // 'search' (this component renders it from /search fetches). Baselines are
  // only ever captured FROM browse mode — a search grid is never a baseline.
  #gridMode = 'browse';
  #resultsList = null;

  // ── Search-results pagination (independent of the paused collection scroll) ──
  // While a query is active the collection's infinite scroll is disconnected, so
  // the swapped-in search results get their OWN paging: a dedicated observer
  // watches a sentinel after the grid and appends /search page N+1 on intersect.
  #searchQuery = '';      // the exact search string currently shown (with scope)
  #searchPage = 1;        // last search page appended
  #searchLastPage = 1;    // data-last-page from the fetched grid (the cap)
  #searchLoading = false;  // guard against overlapping append fetches
  #searchObserver = null;  // IntersectionObserver for the search sentinel
  #searchSentinel = null;  // the watched element

  // Original collection count, captured so an emptied query can restore it.
  // The count ELEMENT is always looked up live (#countEl()) — the collection
  // header can be swapped by #applyServerChrome/morphs, detaching cached refs.
  #originalCount = null;

  #countEl() {
    return document.querySelector('.gg-collection-header__count[data-results-count]');
  }

  // Live lookup of the current product grid <ul>. NOT cached — results-list/morph
  // can swap the node, and a stale ref would write to a detached element.
  #gridEl() {
    return document.querySelector('.product-grid');
  }
  // ── Scope chips (epic fttx) ──────────────────────────────────────────────
  // Visible chips in [data-gg-chips]; each node carries its own URL constraint.
  // kind: 'collection' | 'vendor' (future: suggested tags). Removal REMOVES the
  // node (after the float-out); chips re-enter when their param returns.
  #chips(kind) {
    // A chip animating out (--out) is already logically removed — it must stop
    // contributing constraints the moment it's clicked, not on animationend.
    const sel = `.gg-lookup__chip:not(.gg-lookup__chip--out)${kind ? `[data-chip-kind="${kind}"]` : ''}`;
    return [...this.querySelectorAll(sel)];
  }

  // Locale-aware Shop root (/collections/all).
  #allUrl() {
    return `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}collections/all`;
  }

  // Current URL minus ONE param=value pair (other values of the same param
  // survive — multi-value facets). Also drops stale paging.
  #urlWithout(param, value) {
    const u = new URL(location.href);
    const rest = [...u.searchParams.entries()].filter(([p, v]) => !(p === param && v === value));
    u.search = '';
    for (const [p, v] of rest) u.searchParams.append(p, v);
    u.searchParams.delete('page');
    return `${u.pathname}${u.search}`;
  }

  // Animated chip exit, then REMOVE the node. Chips are re-created from the
  // URL when their param returns — there is no hidden/restorable chip state.
  #dismissChipNode(chip) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      chip.remove();
      return;
    }
    chip.classList.add('gg-lookup__chip--out');
    chip.addEventListener('animationend', () => chip.remove(), { once: true });
  }

  // ── THE state-param definition (single source — everything that reads or
  // rewrites URL state derives from these two; never inline the checks). ──
  // Filter params = the facet state carried across scopes/navigations.
  static #isFilterParam(param) {
    return param.startsWith('filter.');
  }
  // Non-filter params that are also state and must survive scope transitions.
  static #CARRY_PARAMS = ['q', 'sort_by'];

  // Which URL params project as chips: filter.* except price (numeric min/max),
  // availability (value "1", unreadable) and the collection-scope facet (that
  // one IS the collection chip, owned by the pathname).
  static #isChipParam(param, value) {
    return (
      Boolean(value) &&
      GgLookupComponent.#isFilterParam(param) &&
      !param.startsWith('filter.v.price') &&
      param !== 'filter.v.availability' &&
      param !== 'filter.p.m.product_data.collection'
    );
  }

  // Re-derive the filter/vendor chips from the URL (the collection chip is
  // pathname-owned and untouched here). Idempotent; new chips animate in,
  // vanished ones animate out. Labels: facet values ARE their labels for the
  // list facets we project (metafields, vendor, product type).
  #syncChipsFromURL() {
    const box = this.querySelector('[data-gg-chips]');
    const tpl = box?.querySelector('template[data-gg-chip-template]');
    if (!box || !tpl) return;
    const wanted = [...new URLSearchParams(location.search)].filter(([p, v]) =>
      GgLookupComponent.#isChipParam(p, v)
    );
    for (const chip of box.querySelectorAll('.gg-lookup__chip')) {
      if (chip.dataset.chipKind === 'collection' || chip.classList.contains('gg-lookup__chip--out')) continue;
      if (!wanted.some(([p, v]) => p === chip.dataset.chipParam && v === chip.dataset.chipValue)) {
        this.#dismissChipNode(chip);
      }
    }
    for (const [param, value] of wanted) {
      const exists = [...box.querySelectorAll('.gg-lookup__chip:not(.gg-lookup__chip--out)')].some(
        (c) => c.dataset.chipParam === param && c.dataset.chipValue === value
      );
      if (exists) continue;
      const chip = tpl.content.firstElementChild.cloneNode(true);
      chip.dataset.chipKind = param === 'filter.p.vendor' ? 'vendor' : 'filter';
      chip.dataset.chipParam = param;
      chip.dataset.chipValue = value;
      chip.querySelector('.gg-lookup__chip-label').textContent = value;
      box.insertBefore(chip, tpl);
    }
  }

  // Search-mode URL mutations bypass the section render, so the server-rendered
  // nav echoes must be reconciled by hand: category/subcategory hrefs carry the
  // CURRENT filter.* params (gg-shop-carry-query semantics), and the "(brand)"
  // suffix follows filter.p.vendor. In browse mode the facets section render
  // does all of this server-side instead.
  // After a browse fetch, the fetched document is the server truth for the
  // current URL — sync the category nav (active state, subcats, "(brand)"
  // suffix, carried hrefs) and the collection header (title + count) alongside
  // the grid, so nothing on the page contradicts the URL.
  // Cascade the freshly swapped/appended cards in (CSS does the animating —
  // .product-grid--gg-enter + per-card --gg-i stagger, capped so deep grids
  // don't wait). `from` staggers an appended batch relative to itself.
  #animateGridEntrance(grid, from = 0) {
    if (!grid) return;
    grid.classList.add('product-grid--gg-enter');
    const items = grid.querySelectorAll('.product-grid__item');
    for (let i = from; i < items.length; i++) {
      items[i].style.setProperty('--gg-i', Math.min(i - from, 16));
    }
  }

  // "Chrome" = the page FURNITURE (nav, header, facets, chips) — classic web
  // jargon, NOT the Chrome browser. Everything here is browser-agnostic.
  #applyServerChrome(doc) {
    if (!doc) return;
    const swap = (sel) => {
      const next = doc.querySelector(sel);
      const live = document.querySelector(sel);
      if (next && live) live.innerHTML = next.innerHTML;
    };
    // The header swap re-renders the Filter toggle (server default:
    // aria-expanded="false") and the grid-size radios (server default: Normal
    // checked) — capture client state first, re-assert after. This is THE choke
    // point: every live nav and browse render funnels through here.
    const gridVal = document.querySelector('.gg-grid-size__input:checked')?.value;
    swap('.gg-shop-nav__cats-wrap'); // category nav: active state, subcats, carried hrefs
    swap('.gg-collection-header'); // title + count
    swap('.gg-shop-nav__filter'); // facets panel: THIS scope's values/counts (custom elements re-upgrade on insertion)
    this.#reconcileChipsFromDoc(doc); // chips: server truth applied WITHOUT re-inserting survivors
    this.#parseFilterMap(); // the map template rode in with the cats-wrap swap — new scope, new keywords
    this.#syncFilterPanel(this.classList.contains('gg-lookup--filters-open'));
    if (gridVal) {
      document.querySelectorAll('.gg-grid-size__input').forEach((input) => {
        input.checked = input.value === gridVal;
      });
    }
  }

  // Server truth for the chip box WITHOUT innerHTML-blasting it: a blunt swap
  // re-inserts (and re-animates) chips already on screen — the "chip appears,
  // then flashes through its entrance again" bug on category clicks
  // (#patchCollectionChip pre-patches a chip, the swap then replayed gg-float-in
  // on the fresh server node). Reconcile instead: the collection chip matches by
  // KIND (its param flips between product_data.collection and filter.p.vendor
  // for brand collections), facet chips by param+value. Matches are updated in
  // place (no animation); vanished chips float out; only genuinely new server
  // chips are inserted and float in.
  #reconcileChipsFromDoc(doc) {
    const box = this.querySelector('[data-gg-chips]');
    const nextBox = doc.querySelector('[data-gg-chips]');
    if (!box || !nextBox) return;
    const wanted = [...nextBox.querySelectorAll('.gg-lookup__chip')];
    const live = [...box.querySelectorAll('.gg-lookup__chip:not(.gg-lookup__chip--out)')];
    const matchOf = (chip) =>
      wanted.find((w) =>
        chip.dataset.chipKind === 'collection'
          ? w.dataset.chipKind === 'collection'
          : w.dataset.chipKind !== 'collection' &&
            w.dataset.chipParam === chip.dataset.chipParam &&
            w.dataset.chipValue === chip.dataset.chipValue
      );
    const matched = new Set();
    for (const chip of live) {
      const w = matchOf(chip);
      if (!w) {
        this.#dismissChipNode(chip);
        continue;
      }
      matched.add(w);
      chip.dataset.chipKind = w.dataset.chipKind;
      chip.dataset.chipParam = w.dataset.chipParam;
      chip.dataset.chipValue = w.dataset.chipValue;
      const label = chip.querySelector('.gg-lookup__chip-label');
      const nextLabel = w.querySelector('.gg-lookup__chip-label');
      if (label && nextLabel && label.textContent !== nextLabel.textContent) {
        label.textContent = nextLabel.textContent;
      }
    }
    const tpl = box.querySelector('template[data-gg-chip-template]');
    for (const w of wanted) {
      if (matched.has(w)) continue;
      const chip = document.importNode(w, true);
      if (tpl) box.insertBefore(chip, tpl);
      else box.appendChild(chip);
    }
  }

  // ── LIVE scope navigation (Phase B) ───────────────────────────────────────
  // Category clicks become pushState + in-place re-render — the Shop's
  // superpower (live grid) extended to navigation; zero page loads inside the
  // Shop. Real hrefs stay in the DOM (SEO/crawlers/middle-click untouched).
  // Bails out to a REAL navigation for: theme editor (the customizer drives
  // its preview through real loads), modified clicks (new tab etc.), other
  // origins, Marken (different template), and any render failure — a live
  // transition must never strand the user.
  async #liveNavigate(url, event, linkText) {
    if (window.Shopify && window.Shopify.designMode) return;
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) return;
    const u = new URL(url, location.origin);
    if (u.origin !== location.origin || /\/collections\/marken\/?$/.test(u.pathname)) return;
    event?.preventDefault();

    const q = this.input.value.trim();
    if (q) u.searchParams.set('q', q);
    else u.searchParams.delete('q');
    const target = `${u.pathname}${u.search}`;
    history.pushState(history.state, '', target);

    // Instant chip feedback pre-fetch; the browse step of the transition
    // overwrites it with server truth.
    this.#patchCollectionChip(u, linkText);

    const ok = await this.#transitionToURL(q);
    if (ok === false) {
      location.href = target; // graceful degradation to a real navigation
      return;
    }

    // Orient + announce like a navigation would — unless the lookup is STUCK:
    // then the user is browsing mid-grid and a scope change should read like a
    // filter change, not a page load. Land the fresh grid's start (collection
    // header) right under the pinned bar instead of ripping back to the hero;
    // the bar never unsticks, the new results are visible from row 1, and a
    // shorter collection can't strand them past its end.
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const header = document.querySelector('.gg-collection-header');
    if (this.classList.contains('gg-lookup--stuck') && header) {
      const y =
        window.scrollY + header.getBoundingClientRect().top - this.getBoundingClientRect().bottom - 12;
      window.scrollTo({ top: Math.max(0, y), behavior });
    } else {
      window.scrollTo({ top: 0, behavior });
    }
    const announce = document.querySelector('[data-gg-announce]');
    if (announce) {
      announce.textContent = document.querySelector('.gg-collection-header')?.textContent.replace(/\s+/g, ' ').trim() || '';
    }
  }

  // Make the collection chip reflect a pathname we just live-navigated to:
  // /collections/all → no chip; a collection → chip labeled with the clicked
  // link's title (identical to collection.title, which is the facet value).
  #patchCollectionChip(u, linkText) {
    const box = this.querySelector('[data-gg-chips]');
    const tpl = box?.querySelector('template[data-gg-chip-template]');
    let chip = box?.querySelector('.gg-lookup__chip[data-chip-kind="collection"]');
    if (/\/collections\/all\/?$/.test(u.pathname)) {
      chip?.remove();
      return;
    }
    if (!linkText || !box || !tpl) return;
    if (!chip) {
      chip = tpl.content.firstElementChild.cloneNode(true);
      chip.dataset.chipKind = 'collection';
      chip.dataset.chipParam = 'filter.p.m.product_data.collection';
      box.insertBefore(chip, tpl);
    }
    chip.dataset.chipValue = linkText;
    const label = chip.querySelector('.gg-lookup__chip-label');
    if (label) label.textContent = linkText;
  }

  // THE one scope-transition path: re-derive EVERYTHING from the (already
  // updated) URL.
  //   • No query → one browse render (grid + chrome + chips = server truth).
  //   • Query active → the GRID swaps exactly once, straight to the new
  //     results (no "all products" flash): the search render and a parallel
  //     chrome-only fetch of the browse doc run concurrently — nav highlight,
  //     subcats, header, facets and chips are scope-bound (not query-bound),
  //     so applying them independently is safe. The chrome fetch stays outside
  //     the abort chain: even if a newer keystroke supersedes the search, the
  //     chrome is still correct for the URL. Failure = stale chrome until the
  //     next transition (minor) — the search result itself still decides
  //     success/fallback.
  // chromeFirst: history navigations (back/forward) can't pre-patch the
  // collection chip from a clicked link — the chip must come from the server
  // BEFORE the search runs, or the search scopes to the collection being LEFT
  // (QA test 10: correct furniture over wrong results). Click-driven
  // transitions keep the faster parallel path (their chip is already patched).
  async #transitionToURL(q, chromeFirst = false) {
    if (!q) return this.#renderResults('', true);
    const chromeHTML = fetch(this.#browseUrl().toString())
      .then((r) => (r.ok ? r.text() : null))
      .catch(() => null);
    if (chromeFirst) {
      const html = await chromeHTML;
      if (!html) {
        // Can't establish the popped scope's truth → a real load is the only
        // honest fallback (never search under a lying scope).
        location.reload();
        return false;
      }
      this.#applyServerChrome(new DOMParser().parseFromString(html, 'text/html'));
      return this.#renderResults(q); // chips are server-true; count/echo set by the search itself
    }
    const ok = await this.#renderResults(q);
    // Chrome applies LAST (deterministic): the header swap brings the scope's
    // server count, so the search's count + query echo re-assert afterwards.
    const html = await chromeHTML;
    if (html) {
      const searchCount = this.#countEl()?.textContent;
      this.#applyServerChrome(new DOMParser().parseFromString(html, 'text/html'));
      if (ok !== false && this.#gridMode === 'search') {
        const el = this.#countEl();
        if (el && searchCount != null) el.textContent = searchCount;
        this.#setQueryEcho(this.input.value.trim());
      }
    }
    return ok;
  }

  // Back/forward + bfcache: the URL changed under us — same re-derivation,
  // but chrome-FIRST (see #transitionToURL): the popped scope's chips must be
  // server-true before any search runs.
  #onHistoryNav = () => {
    if (!this.#gridEl()) return;
    const q = (new URLSearchParams(location.search).get('q') || '').trim();
    this.input.value = q;
    this.#syncChipsFromURL();
    this.#transitionToURL(q, true);
  };

  // ── No-match presentation (Phase A) ───────────────────────────────────────
  // A managed sibling ABOVE the grid: honest "Keine Ergebnisse" message, plus a
  // clearly-labelled suggestions heading when fallback products are shown
  // (decision: suggestions read better psychologically, but must never read as
  // results). Removed entirely on real matches / restore.
  #setResultNote(kind, query, doc) {
    document.querySelector('.gg-lookup__result-note')?.remove();
    const grid = this.#gridEl();
    if (kind === 'match' || !grid) return;
    const note = document.createElement('div');
    note.className = 'gg-lookup__result-note';
    if (kind === 'empty') {
      // Lift the server's honest empty state (condition guard / zero-result
      // filter) when the fetched doc carries one — server truth, no duplication.
      const lifted = doc?.querySelector('.main-collection-grid__empty');
      if (lifted) note.innerHTML = lifted.outerHTML;
    }
    if (!note.innerHTML) {
      const msg = document.createElement('p');
      msg.className = 'gg-lookup__result-note-msg';
      msg.textContent = (this.dataset.noresultsText || '').replace('@@Q@@', query || '');
      note.append(msg);
    }
    if (kind === 'fallback') {
      const heading = document.createElement('h4');
      heading.className = 'main-collection-grid__title';
      heading.textContent = this.dataset.suggestionsLabel || '';
      note.append(heading);
    }
    grid.before(note);
  }

  // Query echo beside the header count („creme“) — context for the number.
  #setQueryEcho(query) {
    const el = document.querySelector('[data-results-query]');
    if (el) el.textContent = query ? ` · „${query}“` : '';
  }

  #reconcileNavToURL() {
    const current = [...new URLSearchParams(location.search)].filter(([p]) => GgLookupComponent.#isFilterParam(p));
    document.querySelectorAll('.gg-shop-nav__cat[href], .gg-shop-nav__subcat[href]').forEach((a) => {
      const u = new URL(a.getAttribute('href'), location.origin);
      const kept = [...u.searchParams.entries()].filter(([p]) => !GgLookupComponent.#isFilterParam(p));
      u.search = '';
      for (const [p, v] of [...kept, ...current]) u.searchParams.append(p, v);
      a.setAttribute('href', `${u.pathname}${u.search}`);
    });
    if (!current.some(([p]) => p === 'filter.p.vendor')) {
      // data-brand-collection = suffix owned by the PATHNAME (brand collection),
      // not by a vendor param — exempt it from the param-based cleanup.
      document.querySelectorAll('.gg-shop-nav__cat-vendor:not([data-brand-collection])').forEach((el) => el.remove());
    }
  }

  // Base URL for BROWSING (no free-text query) = the current URL itself minus
  // query/paging. The URL is the state, so the browse grid is simply "this
  // page without a search" — pathname carries the collection scope, filter.*
  // params carry the chips. (After a collection-chip removal the pathname is
  // already /collections/all via pushState.)
  #browseUrl() {
    const url = new URL(location.href);
    url.searchParams.delete('q');
    url.searchParams.delete('page');
    return url;
  }

  // ── Typeable filters (epic: concern-filter search, widened in sf87) ─────────
  // Shopify's search index can't read metafields, so filter values (e.g.
  // product_attribute.solution="bei Couperose") are unsearchable by typing.
  // main-collection.liquid emits a keyword→facet map on a <template> inside the
  // nav (parsed by #parseFilterMap, refreshed on every chrome swap); a typed
  // query matching a keyword applies the FILTER instead of free-text /search —
  // authoritative, locale-safe, and engine-independent (works on the broken
  // shopify_search locale too). #collectionUrl is the locale-aware fetch base.
  #filterMap = null;
  #collectionUrl = '/collections/all';

  // ── Rotating typewriter placeholder ──────────────────────────────────────
  // Cycles incentivizing prompts (data-search-prompts, locale-driven, "|"-split)
  // through the input's placeholder. Paused while the field is focused or has a
  // value; fully disabled under prefers-reduced-motion (native placeholder stays).
  #twTimer = null;
  #twPrompts = [];
  #twIndex = 0;
  #twStaticPlaceholder = '';

  connectedCallback() {
    this.input = this.querySelector('.gg-lookup__input');

    if (!this.input) return;

    // NOTE: do NOT cache the grid node here. results-list/morph can replace the
    // .product-grid <ul>, leaving a cached reference detached — writing to it
    // then empties an off-DOM node while the live grid keeps showing everything
    // (this caused "nonsense still shows the whole roster"). Always read it live
    // via #gridEl at swap time.
    // The collection grid lives inside <results-list>, which runs infinite scroll.
    // We pause its IntersectionObserver while a query is active so it can't append
    // more collection pages on top of (or fight) our swapped-in search results.
    this.#resultsList = document.querySelector('results-list');

    // Chip removal — every path is a URL change routed to ONE writer:
    //   collection chip → pushState to /collections/all (+ kept params), then
    //     re-render grid/nav/header in place from the fetched page.
    //   filter/vendor chip, empty input → facets machinery (updateFiltersByURL:
    //     pushState + FilterUpdate + server section render — grid, panel,
    //     category hrefs all come back server-true; our FilterUpdate listener
    //     re-syncs the chips).
    //   filter/vendor chip, query active → this component owns the grid:
    //     replaceState the mutated URL, uncheck the matching panel input,
    //     reconcile nav echoes, re-run the search with fewer constraints.
    this.querySelector('[data-gg-chips]')?.addEventListener('click', (e) => {
      const chip = e.target.closest('.gg-lookup__chip');
      if (!chip || chip.classList.contains('gg-lookup__chip--out')) return;
      const q = this.input.value.trim();
      this.#dismissChipNode(chip); // visual exit; state changes below

      if (chip.dataset.chipKind === 'collection') {
        const u = new URL(this.#allUrl(), location.origin);
        for (const [p, v] of new URLSearchParams(location.search)) {
          if (GgLookupComponent.#isFilterParam(p) || GgLookupComponent.#CARRY_PARAMS.includes(p)) {
            u.searchParams.append(p, v);
          }
        }
        history.pushState(history.state, '', `${u.pathname}${u.search}`);
        this.#transitionToURL(q); // full chrome re-derivation, then the query
        return;
      }

      const url = this.#urlWithout(chip.dataset.chipParam, chip.dataset.chipValue);
      const facetsForm = document.querySelector('facets-form-component');
      if (!q && facetsForm) {
        facetsForm.updateFiltersByURL(url);
        return;
      }
      history.replaceState(history.state, '', url);
      facetsForm?.querySelectorAll(`input[name="${CSS.escape(chip.dataset.chipParam)}"]`).forEach((input) => {
        if (input.value === chip.dataset.chipValue) input.checked = false;
      });
      this.#reconcileNavToURL();
      this.#renderResults(q, q === '');
    });

    // Facet changes from the Filter panel (or any FilterUpdate source): the
    // section render refreshes grid/panel/nav server-side, but the chips live
    // in the morph-skipped search subtree — re-derive them from the new URL.
    // (Literal event name: ThemeEvents.FilterUpdate in events.js — this file
    // is not a module, so it can't import the constant.)
    document.addEventListener('filter:update', () => {
      this.#syncChipsFromURL();
      const q = this.input.value.trim();
      if (q) this.#renderResults(q);
    });
    this.#syncChipsFromURL();

    // Back/forward + bfcache restore (pageshow persisted — Safari's bfcache is
    // aggressive; this keeps restored pages true to their URL).
    window.addEventListener('popstate', this.#onHistoryNav);
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.#onHistoryNav();
    });

    // Build the typeable filter map (re-built after every #applyServerChrome —
    // the source template rides inside the swapped nav, so the map always
    // matches the current scope).
    this.#parseFilterMap();
    this.#collectionUrl =
      this.dataset.collectionUrl ||
      `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}collections/all`;

    this.input.addEventListener('input', this.#debounce(this.#onInput.bind(this), 300));

    // Keep Enter inside the in-page lookup — block the form's native GET to
    // /search so users stay in the Shop grid. Run the in-page search immediately
    // (no debounce) on submit so Enter feels responsive.
    // On a grid-less page (e.g. the Marken directory) there's nothing to swap
    // into, so do NOT hijack — let the form submit natively to /search.
    this.querySelector('.gg-lookup__form')?.addEventListener('submit', (e) => {
      if (!this.#gridEl()) return;
      e.preventDefault();
      this.#renderResults(this.input.value.trim());
    });

    // ── Editorial nav (bean sed4) ──────────────────────────────────────
    // Category click — visual active toggle; stubs prevent scroll-to-top.
    // New Shop page uses .gg-shop-nav__kategorien/.gg-shop-nav__cat; the legacy
    // gg-lookup section (still used by the Marken template) uses
    // .gg-lookup__pills/.gg-lookup__pill — support both.
    // Bind to .gg-shop-nav__cats-wrap (STABLE — #applyServerChrome swaps its
    // innerHTML, replacing the .gg-shop-nav__kategorien nav inside; a listener
    // on the nav itself would die after the first live transition).
    (this.querySelector('.gg-shop-nav__cats-wrap') ||
      this.querySelector('.gg-shop-nav__kategorien') ||
      this.closest('.gg-lookup')?.querySelector('.gg-lookup__pills'))?.addEventListener(
      'click',
      this.#onCategoryClick.bind(this)
    );

    // Marken's divider still carries a Suche tab (focus shortcut); the Shop
    // renders no divider anymore (bean wuhq — tabs died with the docked card).
    this.querySelector('.gg-shop-nav__divider')
      ?.addEventListener('click', this.#onTabClick.bind(this));

    // Restore the Filter panel open/closed state across collection switches
    // (each category link is a full navigation that would otherwise reset it).
    // GATE on the external panel itself: only the Shop renders #gg-filter-panel
    // (Marken has no filters) — a persisted open state can't leak there.
    // An inline pre-paint script in main-collection.liquid may already have added
    // the open classes (to avoid a closed→open shift); in that case
    // #setFiltersOpen no-ops, so sync the projections explicitly here.
    if (document.getElementById('gg-filter-panel')) {
      const stored = sessionStorage.getItem(GgLookupComponent.#FILTERS_OPEN_KEY) === '1';
      if (stored) this.#setFiltersOpen(true);
      this.#syncFilterPanel(stored);

      // Header "Filter" toggle (inline layout). Document-level delegation: the
      // button lives inside .gg-collection-header, whose innerHTML
      // #applyServerChrome swaps on every live nav — a direct binding would die
      // after the first transition. Shop-only (Marken renders no toggle).
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-gg-filter-toggle]')) return;
        this.#setFiltersOpen(!this.classList.contains('gg-lookup--filters-open'));
      });
    }

    // Hydrate from ?q= — a real /search visit redirects here (search-results.liquid)
    // with the query in the URL. Pre-fill the field and run the in-page search so
    // the user lands inside the Shop GG Lookup system with results already showing.
    // Only on a page that actually has a grid to render into (Shop, /search) —
    // skip the render on grid-less pages (e.g. Marken).
    const urlQuery = new URLSearchParams(location.search).get('q');
    if (urlQuery && urlQuery.trim()) {
      this.input.value = urlQuery.trim();
      if (this.#gridEl()) this.#renderResults(urlQuery.trim());
    }

    // Kick off the rotating placeholder (no-op if no prompts / reduced motion /
    // the field already carries a hydrated ?q= value).
    this.#startTypewriter();

    // Sticky stuck-state (Shop only — the sentinel is Shop markup).
    this.#initSticky();

    // Subcat hover-intent (Shop only — Marken has no cats-wrap/tag groups).
    this.#initSubrowHover();
  }

  // Hover-intent for the subcat overlays: pure CSS :hover closed the row the
  // instant the pointer crossed the gap between two main tags — visible
  // flashing. JS owns the pointer-open state (`gg-shop-tag-group--open`):
  // opening is immediate; closing waits a grace period so gap crossings never
  // flicker; entering another group hands over instantly. Keyboard stays pure
  // CSS (:focus-within). Bound to .gg-shop-nav__cats-wrap — stable across
  // #applyServerChrome innerHTML swaps (swapped-in markup arrives closed,
  // which is the correct resting state).
  #subrowCloseTimer = 0;

  #initSubrowHover() {
    const wrap = this.querySelector('.gg-shop-nav__cats-wrap');
    if (!wrap || !window.matchMedia('(hover: hover)').matches) return;
    // Closing is a two-step handshake so it can ANIMATE (display can't):
    // --open → --closing (CSS wipes the panel shut) → classes cleared on
    // animationend, with a timeout backstop (animationend is lost if the tab
    // hides or reduced-motion kills the animation).
    //
    // EVERY pending close is tracked per group and CANCELLED on re-open.
    // Without that, a mid-close re-entry left the old animationend listener +
    // backstop timer armed; the reveal's own animationend (or the 400ms
    // timer) then stripped --open from the freshly opened panel — it flashed
    // away right after opening, and a click aimed at a subcategory landed on
    // nothing (the "hover doesn't load" / "click twice" bugs).
    const pendingClose = new WeakMap();
    const cancelClose = (group) => {
      const p = pendingClose.get(group);
      if (!p) return;
      clearTimeout(p.timer);
      p.subrow?.removeEventListener('animationend', p.onEnd);
      pendingClose.delete(group);
    };
    const finishClose = (group) => {
      cancelClose(group);
      group.classList.remove(
        'gg-shop-tag-group--open',
        'gg-shop-tag-group--closing',
        'gg-shop-tag-group--handover'
      );
    };
    const closeOpen = (instant = false) => {
      wrap.querySelectorAll('.gg-shop-tag-group--open').forEach((group) => {
        if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          finishClose(group);
          return;
        }
        // --handover must go BEFORE --closing lands: its animation:none rule
        // sits later in the cascade and would kill the conceal wipe.
        group.classList.remove('gg-shop-tag-group--open', 'gg-shop-tag-group--handover');
        group.classList.add('gg-shop-tag-group--closing');
        const subrow = group.querySelector('.gg-shop-tag-subrow');
        // animationend BUBBLES — a sub tag's float-in end must not finish the
        // close early, so only the subrow's own conceal wipe counts (the
        // backstop timer calls onEnd with no event).
        const onEnd = (e) => {
          if (e && e.target !== subrow) return;
          finishClose(group);
        };
        const timer = setTimeout(onEnd, 400);
        subrow?.addEventListener('animationend', onEnd);
        pendingClose.set(group, { subrow, onEnd, timer });
      });
    };
    wrap.addEventListener('pointerover', (e) => {
      const group = e.target.closest('.gg-shop-tag-group');
      if (!group) return;
      clearTimeout(this.#subrowCloseTimer);
      // Re-entering a group mid-close cancels the wipe AND its pending
      // finish (listener + backstop).
      const wasClosing = group.classList.contains('gg-shop-tag-group--closing');
      cancelClose(group);
      group.classList.remove('gg-shop-tag-group--closing');
      if (group.classList.contains('gg-shop-tag-group--open')) return;
      // HANDOVER (no reveal wipe) in two cases:
      // • moving from one open category to another — the panel is
      //   conceptually already open (old closes instantly, new skips the
      //   reveal); only the tags float in again to carry the change;
      // • RESCUING this group mid-close — replaying the reveal from zero
      //   here made the panel visibly blink out and back right as the user
      //   aimed at a subcategory; --handover snaps it back fully visible.
      // Opening from nothing = full reveal.
      const handover = wasClosing || Boolean(wrap.querySelector('.gg-shop-tag-group--open'));
      closeOpen(handover);
      group.classList.toggle('gg-shop-tag-group--handover', handover);
      group.classList.add('gg-shop-tag-group--open');
    });
    wrap.addEventListener('pointerout', (e) => {
      const group = e.target.closest('.gg-shop-tag-group');
      if (!group || group.contains(e.relatedTarget)) return;
      clearTimeout(this.#subrowCloseTimer);
      // 300ms grace: a pointer aiming diagonally at a subcategory decelerates
      // near tag boundaries — 160ms closed the panel mid-aim.
      this.#subrowCloseTimer = setTimeout(closeOpen, 300);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#twTimer);
    clearTimeout(this.#subrowCloseTimer);
    this.input?.removeEventListener('focus', this.#twPause);
    this.input?.removeEventListener('blur', this.#twResume);
    this.#stickyObserver?.disconnect();
  }

  // The lookup is position:sticky (CSS); `gg-lookup--stuck` marks "currently
  // pinned" — CSS can't sense that itself. The 1px sentinel right above the
  // component crosses the nav's sticky top line exactly when the nav pins, so
  // one IntersectionObserver with that line as rootMargin is the whole sensor.
  // Class on the component (data-skip-node-update) → survives facet morphs.
  #stickyObserver = null;

  #initSticky() {
    const sentinel = this.previousElementSibling;
    if (!sentinel?.classList.contains('gg-shop-nav-sentinel')) return;
    const stickyTop = Math.ceil(parseFloat(getComputedStyle(this).top) || 0);
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('gg-lookup--stuck', !entry.isIntersecting);
        // Unsticking always restores visibility — the hide is a stuck-only state.
        if (entry.isIntersecting) this.classList.remove('gg-lookup--hidden');
      },
      { rootMargin: `-${stickyTop + 1}px 0px 0px 0px` }
    );
    this.#stickyObserver.observe(sentinel);

    // Scroll-direction visibility while stuck: scrolling DOWN = browsing —
    // the bar slides away and gives the products the full viewport; a
    // DELIBERATE scroll up brings it back. Directional run counters (reset on
    // reversal) give hysteresis. The reveal threshold is intentionally chunky:
    // small upward corrections happen constantly while browsing downward, and
    // a hair-trigger reveal made the bar pump in and out.
    const HIDE_AFTER = 60; // px of downward travel before the bar leaves
    const SHOW_AFTER = 120; // px of upward travel before it returns
    let lastY = window.scrollY;
    let downRun = 0;
    let upRun = 0;
    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        if (!this.classList.contains('gg-lookup--stuck') || delta === 0) return;
        if (delta > 0) {
          downRun += delta;
          upRun = 0;
          if (downRun > HIDE_AFTER) this.classList.add('gg-lookup--hidden');
        } else {
          upRun -= delta;
          downRun = 0;
          if (upRun > SHOW_AFTER) this.classList.remove('gg-lookup--hidden');
        }
      },
      { passive: true }
    );
  }

  // ── Typewriter implementation ────────────────────────────────────────────
  #startTypewriter() {
    const raw = (this.dataset.searchPrompts || '').trim();
    if (!raw) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#twPrompts = raw.split('|').map((s) => s.trim()).filter(Boolean);
    if (this.#twPrompts.length === 0) return;

    this.#twStaticPlaceholder = this.input.getAttribute('placeholder') || '';
    this.input.addEventListener('focus', this.#twPause);
    this.input.addEventListener('blur', this.#twResume);

    this.#twIndex = 0;
    this.#twStep(this.#twPrompts[0], 0, 'typing');
  }

  // Animation runs only when the field is idle (unfocused + empty); otherwise it
  // bails and waits for #twResume (on blur of an empty field) to restart.
  #twIdle() {
    return document.activeElement !== this.input && !this.input.value;
  }

  #twStep = (text, pos, phase) => {
    if (!this.#twIdle()) return;
    const TYPE = 55, ERASE = 28, HOLD = 1600, BETWEEN = 380;
    if (phase === 'typing') {
      this.input.setAttribute('placeholder', text.slice(0, pos + 1));
      if (pos + 1 < text.length) {
        this.#twTimer = setTimeout(() => this.#twStep(text, pos + 1, 'typing'), TYPE);
      } else {
        this.#twTimer = setTimeout(() => this.#twStep(text, text.length, 'erasing'), HOLD);
      }
    } else {
      this.input.setAttribute('placeholder', text.slice(0, Math.max(pos - 1, 0)));
      if (pos - 1 > 0) {
        this.#twTimer = setTimeout(() => this.#twStep(text, pos - 1, 'erasing'), ERASE);
      } else {
        this.#twIndex = (this.#twIndex + 1) % this.#twPrompts.length;
        const next = this.#twPrompts[this.#twIndex];
        this.#twTimer = setTimeout(() => this.#twStep(next, 0, 'typing'), BETWEEN);
      }
    }
  };

  #twPause = () => {
    clearTimeout(this.#twTimer);
    this.#twTimer = null;
    // Leave a clean, complete placeholder while the user is in the field.
    this.input.setAttribute('placeholder', this.#twStaticPlaceholder);
  };

  #twResume = () => {
    if (this.input.value) return; // user typed something — keep static, no rotation
    clearTimeout(this.#twTimer);
    this.#twIndex = 0;
    this.#twStep(this.#twPrompts[0], 0, 'typing');
  };

  // ── Category list: active toggle + all-products redirect (was #onPillClick) ──
  // Supports both the new .gg-shop-nav__cat and the legacy .gg-lookup__pill.
  #onCategoryClick(e) {
    // Subcollection links live in the same nav — same live transition.
    const sub = e.target.closest('.gg-shop-nav__subcat');
    if (sub && sub.href) {
      this.#liveNavigate(sub.href, e, sub.textContent.trim());
      return;
    }

    const cat = e.target.closest('.gg-shop-nav__cat, .gg-lookup__pill');
    if (!cat) return;

    const isNew = cat.classList.contains('gg-shop-nav__cat');
    const itemSel = isNew ? '.gg-shop-nav__cat' : '.gg-lookup__pill';
    const activeCls = isNew ? 'gg-shop-nav__cat--active' : 'gg-lookup__pill--active';
    const stubCls = isNew ? 'gg-shop-nav__cat--stub' : 'gg-lookup__pill--stub';
    const nav = cat.closest('.gg-shop-nav__kategorien, .gg-lookup__pills');
    const isStub = cat.classList.contains(stubCls);

    if (isStub) e.preventDefault();

    // Deselect-to-all fires ONLY when the clicked category IS the current
    // collection (compare resolved pathnames). On a SUBcollection page the PARENT
    // main is highlighted (--active) but its href points at the parent collection,
    // so a class-based check would wrongly send it to /all — the path check sends
    // it to the parent instead. Stubs (href="#") never deselect.
    let onThisCollection = false;
    if (!isStub) {
      try {
        onThisCollection = new URL(cat.href, location.origin).pathname === location.pathname;
      } catch (_) { /* missing/malformed href → treat as not-current */ }
    }

    if (onThisCollection) {
      e.preventDefault();
      nav.querySelectorAll(itemSel).forEach(c => c.classList.remove(activeCls));
      // Deselect-to-all is a scope transition like any other — live (Phase B).
      this.#liveNavigate(nav.dataset.allUrl, null);
      return;
    }

    // Instant visual feedback; #applyServerChrome overwrites with server truth.
    nav.querySelectorAll(itemSel).forEach(c => c.classList.toggle(activeCls, c === cat));

    // LIVE navigation (Phase B): same-template collection links render in
    // place. #liveNavigate preventDefaults ONLY when it takes over — stubs,
    // modified clicks, Marken, the theme editor and cross-origin links keep
    // their native behavior.
    if (!isStub && isNew && cat.href) {
      this.#liveNavigate(cat.href, e, cat.textContent.trim());
    }
  }

  // ── Divider tabs — Marken only now (the Shop renders no divider since bean
  // wuhq killed the docked card): the Suche tab focuses the always-visible field. ──
  #onTabClick(e) {
    const tab = e.target.closest('.gg-shop-nav__tab');
    if (tab?.dataset.tab === 'suche') this.input?.focus();
  }

  // Filter open/closed — ONE state, two projections. The canonical class
  // `gg-lookup--filters-open` lives on this component (data-skip-node-update, so
  // it survives facet morphs); the external inline panel (#gg-filter-panel,
  // Shop only — sits between the collection header and the grid) mirrors it as
  // `is-open`, which CSS turns into the 0fr→1fr height expansion. No JS
  // animation.
  #setFiltersOpen(open) {
    if (open === this.classList.contains('gg-lookup--filters-open')) return;
    this.classList.toggle('gg-lookup--filters-open', open);
    this.#syncFilterPanel(open);

    // Persist so the chosen view carries across collection-switch navigations.
    try {
      sessionStorage.setItem(GgLookupComponent.#FILTERS_OPEN_KEY, open ? '1' : '0');
    } catch (_) { /* storage unavailable — degrade to non-persistent */ }
  }

  // Project the open state onto the external panel + the header toggle button.
  // Callable on its own because both nodes can be re-rendered out from under us
  // (#applyServerChrome innerHTML-swaps the collection header on every live nav,
  // resetting the server-rendered aria-expanded="false").
  #syncFilterPanel(open) {
    document.getElementById('gg-filter-panel')?.classList.toggle('is-open', open);
    document
      .querySelector('[data-gg-filter-toggle]')
      ?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  #onInput(e) {
    this.#renderResults(e.target.value.trim());
  }

  // Pause the results-list infinite scroll so it stops appending collection pages
  // while we show search results.
  #pauseInfiniteScroll() {
    this.#resultsList?.infinityScrollObserver?.disconnect();
  }

  // Resume infinite scroll after restoring the original grid: re-observe the
  // freshly-restored "view more" sentinels with the existing observer.
  #resumeInfiniteScroll() {
    const obs = this.#resultsList?.infinityScrollObserver;
    if (!obs || !this.#resultsList?.refs) return;
    const { viewMorePrevious, viewMoreNext } = this.#resultsList.refs;
    if (viewMorePrevious) obs.observe(viewMorePrevious);
    if (viewMoreNext) obs.observe(viewMoreNext);
  }

  // Update the header product-count to reflect the active search (the total number
  // of matches, not just the loaded page). Captures the original collection count
  // once so an emptied query can restore it.
  #setCount(n) {
    const el = this.#countEl();
    if (!el) return;
    if (this.#originalCount === null) this.#originalCount = el.textContent;
    el.textContent = n;
  }

  #restoreCount() {
    const el = this.#countEl();
    if (el && this.#originalCount !== null) {
      el.textContent = this.#originalCount;
      this.#originalCount = null;
    }
  }

  // browse=true: empty query but the state changed (a chip was removed) —
  // fetch the browse grid for the CURRENT URL instead of restoring a cache.
  async #renderResults(query, browse = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#teardownSearchPaging();

    const grid = this.#gridEl();
    if (!grid) return false;

    if (!query && !browse) {
      // Cleared search → back to browse mode. Drop ?q= from the URL, then
      // restore the cached grid ONLY if it was captured under this exact URL —
      // if chips/facets changed meanwhile, the cache is stale and the browse
      // grid is fetched fresh instead (no ghost filters, ever).
      const u = new URL(location.href);
      u.searchParams.delete('q');
      history.replaceState(history.state, '', `${u.pathname}${u.search}`);
      if (this.#originalGridHTML !== null && this.#originalGridURL === this.#browseUrl().href) {
        grid.innerHTML = this.#originalGridHTML;
        this.#animateGridEntrance(grid);
        this.#originalGridHTML = null;
        this.#originalGridURL = null;
        this.#gridMode = 'browse';
        this.#setResultNote('match');
        this.#setQueryEcho('');
        this.#resumeInfiniteScroll();
        this.#restoreCount();
        return true;
      }
      this.#originalGridHTML = null;
      this.#originalGridURL = null;
      browse = true;
    }

    // QUERY → CHIP (Phase A, widened in sf87): a typed facet value becomes a
    // VISIBLE facet chip instead of a free-text fetch — the query became a
    // filter (token-lifting; Phase C generalizes this). Tier decides WHEN:
    // instant keywords (Marke, Zertifizierung, concerns …) apply right here;
    // common-word keywords ("Glas", "Rot") let the real search run first and
    // only convert on a no-match (below, after the fetch) — a successful
    // search is never hijacked. Falls through to the legacy route in
    // #fetchSearchPage when the facets component isn't available.
    let commonMatch = null;
    if (query) {
      const match = this.#matchFilterKeyword(ggCorrectConditionQuery(query));
      if (match && match.tier === 'i' && this.#applyFilterMatch(match)) return true;
      if (match && match.tier === 'c') commonMatch = match;
    }

    // Baselines are only captured FROM browse mode (a search grid is never a
    // baseline), stamped with the browse-normalized URL they belong to (q/page
    // stripped — a hydrated ?q= load still captures a valid browse grid).
    if (query && this.#gridMode === 'browse') {
      this.#originalGridHTML = grid.innerHTML;
      this.#originalGridURL = this.#browseUrl().href;
    }
    this.#pauseInfiniteScroll();

    this.classList.add('gg-lookup--loading');

    // Scope to the current collection with a real FACET param (server-side
    // intersection) — NOT an `AND tag:` search term. The term form is a loose
    // relevance signal (inconsistent matches) AND locale-broken (the tag facet is
    // default-language only → 0 on /en). The facet `filter.p.m.product_data.collection`
    // reads a metafield Shopify syncs to collection membership nightly: a hard
    // filter, translatable, exact 1:1 with the collection. The query stays CLEAN.
    const searchQuery = query;

    try {
      const { kind, grid: gridHTML, lastPage, count, doc } = await this.#fetchSearchPage(
        searchQuery,
        1,
        this.#abortController.signal
      );
      // COMMON-TIER CONVERSION: the real search ran and found nothing, but the
      // typed word names a facet value ("Glas", or a typo of one) — apply the
      // filter instead of painting the no-match state. Instant-tier keywords
      // never reach here (applied before the fetch).
      if (kind !== 'match' && commonMatch && this.#applyFilterMatch(commonMatch)) {
        return true; // finally-block clears the loading state
      }
      // SCOPE-AWARE BESTSELLER suggestions: a no-match suggests the current
      // scope's BESTSELLERS (collection URLs sort natively via
      // ?sort_by=best-selling; unscoped = store-wide bestsellers on /all).
      // One extra fetch, only on the rare no-match path. Fallback chain if it
      // fails: the pre-search baseline grid (the scoped browse view, already
      // captured, zero fetch) → the server's generic empty-state set.
      let cards = gridHTML;
      if (kind === 'fallback') {
        try {
          const bu = this.#browseUrl();
          bu.searchParams.set('sort_by', 'best-selling');
          const bres = await fetch(bu.toString(), { signal: this.#abortController.signal });
          if (bres.ok) {
            const best = this.#extractGrid(await bres.text());
            if (best.kind === 'match' && best.grid) cards = best.grid;
          }
        } catch (bestErr) {
          if (bestErr.name === 'AbortError') throw bestErr; // superseded — bail out
        }
        if (
          cards === gridHTML &&
          this.#chips().length &&
          this.#originalGridHTML !== null &&
          this.#originalGridURL === this.#browseUrl().href
        ) {
          cards = this.#originalGridHTML;
        }
      }
      // Visual application — one unit so browse transitions can crossfade it.
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const useVT = !query && !reducedMotion && typeof document.startViewTransition === 'function';
      const paint = () => {
        // Re-read the grid: a morph during the await may have replaced the node.
        const liveGrid = this.#gridEl() ?? grid;
        liveGrid.innerHTML = cards; // fallback keeps its cards; empty = ''
        // Card cascade so the change reads — unless the view-transition
        // crossfade already communicates it (no double animation).
        if (!useVT) this.#animateGridEntrance(liveGrid);
        // No-match presentation (message / suggestions) above the grid.
        this.#setResultNote(kind, query, doc);
        if (!query) {
          // Browse: nav + header + facets + chips sync from the fetched doc.
          this.#applyServerChrome(doc);
          this.#setQueryEcho('');
        }
      };
      if (useVT) {
        // Feature-guarded (older Safari/Firefox take the paint() path with the
        // card cascade instead).
        await document.startViewTransition(paint).updateCallbackDone;
      } else {
        paint();
      }

      if (query) {
        // Search mode: this component owns the grid. Project the query into
        // the URL so reload/share reproduces exactly this state (?q= hydration
        // in connectedCallback picks it up).
        this.#gridMode = 'search';
        const u = new URL(location.href);
        u.searchParams.set('q', query);
        history.replaceState(history.state, '', `${u.pathname}${u.search}`);
        // True match total in the header (0 on no-match) + the query echo.
        this.#setCount(kind === 'match' ? count : 0);
        this.#setQueryEcho(query);
      } else {
        // Browse fetch: the fetched page IS the server truth for the current
        // URL — the swapped grid is the new baseline (nothing to restore).
        this.#gridMode = 'browse';
        this.#originalGridHTML = null;
        this.#originalGridURL = null;
        this.#originalCount = null;
      }

      // Own pagination ONLY for real matches (fallback suggestions are page 1
      // by design — paging them would make them read like results). Browse
      // pages re-enter the browse route via searchQuery ''.
      if (kind === 'match') {
        this.#searchQuery = searchQuery;
        this.#searchPage = 1;
        this.#searchLastPage = lastPage;
        if (lastPage > 1) this.#setupSearchPaging();
      }
      return true;
    } catch (err) {
      // Aborts = superseded by a newer render — not a failure (true keeps
      // #liveNavigate from force-navigating away from the newer state).
      if (err.name === 'AbortError') return true;
      // Network/HTTP failure → restore original rather than leave a blank grid.
      if (this.#originalGridHTML !== null) (this.#gridEl() ?? grid).innerHTML = this.#originalGridHTML;
      this.#setResultNote('match');
      this.#setQueryEcho('');
      this.#restoreCount();
      return false;
    } finally {
      this.classList.remove('gg-lookup--loading');
    }
  }

  // Parse the typed-filter map from its <template> inside .gg-shop-nav__cats-wrap
  // into #filterMap: "ecocert" → { param, value, tier }. COMPACT format (audit
  // 2026-07-06): param names ship once (data-gg-filter-params, ||-joined) and
  // entries reference them by index — keyword~~paramIndex~~value~~tier, joined
  // by || (tier: i = apply instantly, c = common word — search first, filter
  // only on no-match). Keys are diacritic-folded so "parfumol" can hit
  // "Parfümöl"; first key wins on fold collisions (deterministic; ambiguity
  // resolved server-side by facet order). Missing template (Marken) = empty map.
  #parseFilterMap() {
    this.#filterMap = new Map();
    const src = this.querySelector('template[data-gg-filter-map]');
    if (!src) return;
    const params = (src.dataset.ggFilterParams || '').split('||');
    (src.dataset.ggFilterMap || '').split('||').forEach((e) => {
      if (!e) return;
      const i = e.indexOf('~~');
      const j = e.indexOf('~~', i + 2);
      if (i < 0 || j < 0) return;
      const k = GgLookupComponent.#foldQuery(e.slice(0, i));
      const p = params[Number(e.slice(i + 2, j))];
      let v = e.slice(j + 2);
      let tier = 'i';
      const t = v.lastIndexOf('~~');
      if (t >= 0) {
        tier = v.slice(t + 2) === 'c' ? 'c' : 'i';
        v = v.slice(0, t);
      }
      if (k && p && v && !this.#filterMap.has(k)) {
        this.#filterMap.set(k, { param: p, value: v, tier });
      }
    });
  }

  // Normalize for keyword matching: lowercase, collapse whitespace, fold
  // diacritics + ß ("parfumol" must hit "Parfümöl", "suss" → "süß").
  static #foldQuery(s) {
    return s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Levenshtein distance, capped: returns max+1 as soon as the answer can't
  // be ≤ max (row-minimum bail) — the loop below calls this a few hundred
  // times per keystroke at most, on ~10-char strings.
  static #editDistance(a, b, max) {
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
      const cur = [i];
      let rowMin = i;
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (cur[j] < rowMin) rowMin = cur[j];
      }
      if (rowMin > max) return max + 1;
      prev = cur;
    }
    return prev[b.length];
  }

  // If the (corrected) full query names a typeable filter keyword, return
  // { param, value, tier }; else null. Exact (folded) match first; otherwise a
  // guarded fuzzy pass for typos ("Eccocert" → Ecocert). The guards are what
  // keep LIVE fuzzy safe without waiting for a failed search:
  //   • length ≥ 5 and |len(query) − len(keyword)| ≤ 1 — a typo keeps its
  //     length; a HALF-TYPED word is much shorter than its keyword, so nothing
  //     fires mid-word while typing toward it;
  //   • first letter must match; distance ≤ 1 (5–7 chars) / ≤ 2 (8+);
  //   • unique winner — a tie between two different filter targets applies
  //     nothing (the free search runs instead of guessing).
  // Full-query match only (no substring), so multi-word searches are never
  // affected.
  #matchFilterKeyword(query) {
    if (!this.#filterMap || this.#filterMap.size === 0) return null;
    const q = GgLookupComponent.#foldQuery(query);
    const exact = this.#filterMap.get(q);
    if (exact) return exact;
    if (q.length < 5) return null;
    const max = q.length >= 8 ? 2 : 1;
    let best = null;
    let bestDist = max + 1;
    let tie = false;
    for (const [k, target] of this.#filterMap) {
      if (k[0] !== q[0] || Math.abs(k.length - q.length) > 1) continue;
      const d = GgLookupComponent.#editDistance(q, k, max);
      if (d > max) continue;
      if (d < bestDist) {
        best = target;
        bestDist = d;
        tie = false;
      } else if (d === bestDist && (target.param !== best.param || target.value !== best.value)) {
        tie = true;
      }
    }
    return tie ? null : best;
  }

  // Apply a matched filter keyword: the query becomes a facet chip (the input
  // clears — token lifting) and the facets machinery renders the filtered grid
  // server-true. Returns false when the facets component isn't available (the
  // caller falls through to its legacy route).
  #applyFilterMatch(match) {
    const facetsForm = document.querySelector('facets-form-component');
    if (!match || typeof facetsForm?.updateFiltersByURL !== 'function') return false;
    this.input.value = '';
    this.#setQueryEcho('');
    const already = new URLSearchParams(location.search).getAll(match.param).includes(match.value);
    if (already) return true; // chip is already applied — nothing to change
    const u = this.#browseUrl();
    u.searchParams.append(match.param, match.value);
    facetsForm.updateFiltersByURL(`${u.pathname}${u.search}`);
    return true;
  }

  // Fetch one /search results page and return { grid, lastPage }.
  //   grid     — the .product-grid innerHTML, or '' for a confirmed no-match.
  //   lastPage — total pages (from the grid's data-last-page), 1 if absent.
  //
  // We fetch the real /search page — NO predictive suggest.json gate. /search is
  // the single source of truth: fuzzy (typo-tolerant) and searches the description
  // (body), both of which the suggest API is stricter about, so a suggest gate
  // produced false negatives (e.g. "serm" or ingredient terms → 0 from suggest
  // while /search had hundreds).
  //
  // We fetch the FULL /search page (not section_id rendering). The Section
  // Rendering API does NOT resolve product.featured_media or the nested
  // _product-card-gallery block in isolated renders — verified on BOTH the search
  // and collection templates — so a section render returns image-LESS cards. The
  // full page renders media correctly. It's ~825 KB (we keep only .product-grid),
  // but correctness beats the size win. (Verified live 2026-06-18.) The page
  // supports &page=N for pagination and carries data-last-page.
  async #fetchSearchPage(searchQuery, page, signal) {
    // BROWSE ROUTE (empty query): a chip was removed with an empty input — fetch
    // the wider grid for the remaining visible chips. Collection pages return
    // the same .product-grid with data-last-page/data-results-count, so extraction
    // and paging work unchanged (the paging append re-enters here with '' too).
    if (!searchQuery) {
      const burl = this.#browseUrl();
      if (page > 1) burl.searchParams.set('page', page);
      const bres = await fetch(burl.toString(), { signal });
      if (!bres.ok) throw new Error(`browse ${bres.status}`);
      return this.#extractGrid(await bres.text());
    }

    // Auto-correct misspelled condition terms so we search the CANONICAL spelling
    // (e.g. "Cuperose" → "Couperose") and surface the right products instead of
    // letting semantic search broaden the typo into the wrong category.
    searchQuery = ggCorrectConditionQuery(searchQuery);

    // FILTER-KEYWORD ROUTE (legacy fallback — #renderResults' Phase A chip route
    // normally intercepts first): if the (corrected) query names an INSTANT-tier
    // filter keyword, serve it from the FILTER on the current collection — NOT
    // free-text search. Shopify's search index can't read metafields, and the
    // filter is authoritative + engine-independent (correct on the broken
    // shopify_search locale). Common-tier ('c') keywords deliberately fall
    // through to the real search here — the no-match conversion is handled by
    // #renderResults, and this route must never hijack a working search.
    // The filtered collection page returns the same .product-grid (with accurate
    // data-results-count/last-page — no condition-guard miscount here).
    const concern = this.#matchFilterKeyword(searchQuery);
    if (concern && concern.tier === 'i') {
      // Base = #browseUrl(): the collection page while its chip is on (same URL
      // as before), /collections/all after removal — vendor chips ride along.
      const furl = this.#browseUrl();
      furl.searchParams.set(concern.param, concern.value);
      if (page > 1) furl.searchParams.set('page', page);
      const fres = await fetch(furl.toString(), { signal });
      if (!fres.ok) throw new Error(`filter ${fres.status}`);
      return this.#extractGrid(await fres.text());
    }

    // LOCALE-AWARE search path. A hardcoded '/search' always hits the ROOT
    // (default-locale) search index — so on the EN storefront (/en/…) it queried
    // the German index and returned the wrong/short result set (e.g. "reindex"
    // 625 vs the true 3380). Build the path from Shopify's locale root instead:
    // '/' → '/search', '/en/' → '/en/search'. Mirrors the form's
    // `routes.search_url`, so the JS fetch and the native form agree per locale.
    const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const searchPath = `${localeRoot.replace(/\/?$/, '/')}search`;
    const url = new URL(searchPath, location.origin);
    url.searchParams.set('q', searchQuery);
    url.searchParams.set('type', 'product');
    // Every visible chip contributes its facet param (bare keys, NOT [] brackets
    // — Shopify ignores the bracketed form on /search). The collection chip's
    // value is the collection TITLE, exactly what the product_data.collection
    // metafield stores. NB: filter.p.vendor is default-locale-only on /search
    // (correct on DE, silently unscoped on /en) — the metafield facet is locale-safe.
    for (const c of this.#chips()) {
      url.searchParams.set(c.dataset.chipParam, c.dataset.chipValue);
    }
    if (page > 1) url.searchParams.set('page', page);

    const res = await fetch(url.toString(), { signal });
    // Throw on a bad response so the caller restores the original grid
    // (distinct from a confirmed no-match, which returns grid: '').
    if (!res.ok) throw new Error(`search ${res.status}`);
    return this.#extractGrid(await res.text());
  }

  // Lift the .product-grid out of a fetched /search section. Returns
  // { grid: innerHTML | '', lastPage: number }.
  //
  // CRITICAL no-match detection: on a true no-match, search-results.liquid swaps to
  // the empty_state_collection and FILLS .product-grid with fallback products — so
  // counting items is NOT a no-match signal (verified: nonsense → 50 fallback
  // cards). The authoritative, locale-independent marker is [data-gg-no-results],
  // a hidden element search-results.liquid emits ONLY in the no-match branch
  // specifically for this contract (so it can't be removed by an unrelated
  // cleanup). `.main-collection-grid__title` is kept as a defensive secondary
  // signal (the no-match branch also passes a `title` to product-grid), but it is
  // incidental — do not depend on it alone. (Note: an older comment claimed
  // `.search-results__no-results` was a marker here; it never appeared in the
  // search-results template gg-lookup fetches — that class lives only in the
  // predictive-search modal — so it was never a real signal.)
  #extractGrid(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // No-match is signalled ONLY by the intentional [data-gg-no-results] marker
    // search-results.liquid emits in its empty/guard branches. (Dropped the old
    // secondary check on .main-collection-grid__title — a landmine: the match
    // branch may legitimately render a title, which would then false-flag a full
    // grid as a no-match and blank real results.)
    const isNoMatch = doc.querySelector('[data-gg-no-results]');
    const grid = doc.querySelector('.product-grid');
    const hasItems = !!grid?.querySelector('.product-grid__item');
    if (isNoMatch) {
      // Two server no-match shapes (search-results.liquid):
      //   'fallback' — marker + a grid of recommendation products (generic
      //                no-match). Shown as SUGGESTIONS under an honest message.
      //   'empty'    — marker + no items (condition guard / true empty). The
      //                doc carries the honest empty-state markup to lift.
      if (hasItems) {
        return { kind: 'fallback', grid: grid.innerHTML, lastPage: 1, count: 0, doc };
      }
      return { kind: 'empty', grid: '', lastPage: 1, count: 0, doc };
    }
    if (!hasItems) {
      return { kind: 'empty', grid: '', lastPage: 1, count: 0, doc };
    }
    const lastPage = parseInt(grid.getAttribute('data-last-page'), 10) || 1;
    const count = parseInt(grid.getAttribute('data-results-count'), 10) || 0;
    return { kind: 'match', grid: grid.innerHTML, lastPage, count, doc };
  }

  // ── Search-results own pagination ──────────────────────────────────────────
  // A sentinel after the grid, watched by a dedicated observer; on intersect we
  // append the next /search page. Independent of the (paused) collection scroll.
  #setupSearchPaging() {
    const grid = this.#gridEl();
    if (!grid) return;
    this.#searchSentinel = document.createElement('div');
    this.#searchSentinel.className = 'gg-lookup__search-sentinel';
    this.#searchSentinel.setAttribute('aria-hidden', 'true');
    grid.after(this.#searchSentinel);

    this.#searchObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.#appendNextSearchPage();
      },
      { rootMargin: '600px' } // prefetch before it scrolls into view
    );
    this.#searchObserver.observe(this.#searchSentinel);
  }

  #teardownSearchPaging() {
    this.#searchObserver?.disconnect();
    this.#searchObserver = null;
    this.#searchSentinel?.remove();
    this.#searchSentinel = null;
    this.#searchLoading = false;
    this.#searchQuery = '';
    this.#searchPage = 1;
    this.#searchLastPage = 1;
  }

  async #appendNextSearchPage() {
    if (this.#searchLoading) return;
    if (this.#searchPage >= this.#searchLastPage) {
      this.#teardownSearchPaging(); // exhausted — stop observing
      return;
    }
    this.#searchLoading = true;
    const nextPage = this.#searchPage + 1;
    try {
      const { grid: gridHTML } = await this.#fetchSearchPage(
        this.#searchQuery,
        nextPage,
        this.#abortController.signal
      );
      const grid = this.#gridEl();
      if (!grid || gridHTML === '') {
        this.#teardownSearchPaging();
        return;
      }
      // Append only the new cards (parse the fragment, move its <li>s in).
      const frag = new DOMParser().parseFromString(`<ul>${gridHTML}</ul>`, 'text/html');
      const items = frag.querySelectorAll('.product-grid__item');
      if (items.length === 0) {
        this.#teardownSearchPaging();
        return;
      }
      items.forEach((li) => grid.appendChild(li));
      // Cascade the appended batch (staggered relative to itself).
      this.#animateGridEntrance(grid, grid.children.length - items.length);
      this.#searchPage = nextPage;
      // Keep the sentinel after the freshly grown grid and RE-OBSERVE it: an
      // IntersectionObserver won't re-fire while an element stays continuously
      // intersecting, so if the sentinel is still on-screen after appending we'd
      // stall. unobserve→observe forces a fresh intersection check, which loads
      // the next page if it's still visible (until the grid grows past viewport
      // or pages are exhausted).
      if (this.#searchSentinel && this.#searchObserver) {
        grid.after(this.#searchSentinel);
        this.#searchObserver.unobserve(this.#searchSentinel);
        this.#searchObserver.observe(this.#searchSentinel);
      }
    } catch (err) {
      if (err.name !== 'AbortError') this.#teardownSearchPaging();
    } finally {
      this.#searchLoading = false;
    }
  }

  #debounce(fn, ms) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  }
}

// ─────────────────────────────────────────────────────────────────
// PHASE C — Inline Predictive Search dropdown  (bean: greenglam-thestore-glke)
// ─────────────────────────────────────────────────────────────────
// Extend #onInput to also show a predictive dropdown above the grid.
// GET /search/suggest.json?q={query}&resources[type]=product&resources[limit]=6
// Render product thumbnails + titles in a .gg-lookup__results overlay below the input.
//
// PHASE E — AI Advisory Layer  (bean: greenglam-thestore-glkg)
// ─────────────────────────────────────────────────────────────────
// Activate: ADD data-ai-endpoint="/apps/gg-lookup/search" data-ai-enabled="true"
// to <gg-lookup-component> plus an answer container (the placeholder markup was
// stripped 2026-07-06 — parked attrs/divs shipped on every page for nothing).
// Intercept submit for advisory queries (skin conditions / ingredients / questions).
// POST endpoint → { answer, employee: { name, role, avatar_url }, products: [] }
// Render the answer, then re-run #filterProducts with the tag filter.

if (!customElements.get('gg-lookup-component')) {
  customElements.define('gg-lookup-component', GgLookupComponent);
}
